import { useEffect, useRef, useState, type PointerEvent } from "react";
import { cn } from "@shared/lib/utils";

/** Blender 렌더 타임라인(30fps): f1~72 플로팅 루프(심리스), f73~ 찢김·카드 배출 */
const LOOP_END = 2.4; // 플로팅 루프 끝(f72) — 시작 포즈와 동일해 0으로 심리스 되감기

/**
 * 팩 오프닝 시네마틱 — Blender 풀 렌더(EEVEE·카메라 워크·글레어) 영상.
 * 연출은 전부 Blender 씬(scripts/pack-render-cinematic.py)에서 렌더되고 웹은 재생만.
 * 유저가 팩을 스와이프/클릭해 직접 뜯는다 — 그 전까지 셰이크 구간을 루프.
 */
export function PackOpenVideo({
  packId,
  rare = false,
  onDone,
  onTear,
}: {
  packId: string;
  /** 상위 희귀 포함 풀 — 프리즘 팔레트 변형(리빌 전 희귀도 컬러 신호) */
  rare?: boolean;
  onDone: () => void;
  /** 유저가 팩을 실제로 찢은 순간(제스처 보장) — SFX unlock·햅틱 훅 */
  onTear?: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const torn = useRef(false);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const [waiting, setWaiting] = useState(true);

  // rAF로 프레임 단위 감시 — 찢김 직전에서 셰이크 구간으로 되감아 대기 루프
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const v = video.current;
      if (v && !torn.current && v.currentTime >= LOOP_END) v.currentTime = 0;
      if (!torn.current) raf = requestAnimationFrame(tick); // 찢은 뒤엔 감시 불필요 — rAF 종료
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const tear = () => {
    if (torn.current) return;
    torn.current = true;
    setWaiting(false);
    onTear?.();
    const v = video.current;
    if (v) {
      // 유저 제스처 안 — 오디오 트랙 있는 렌더라면 음소거 해제 허용됨
      v.muted = false;
      v.currentTime = LOOP_END; // 찢김 시퀀스 시작
      void v.play();
    }
  };

  // 팩 '상단부'를 가로로 드래그해야 뜯김 — 상단 55% 영역에서 시작한 가로 드래그만 인정
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const yRatio = (e.clientY - rect.top) / rect.height;
    if (yRatio < 0.55) drag.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const dx = Math.abs(e.clientX - drag.current.x);
    if (dx > 50) tear(); // 가로 드래그 = 찢기
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  return (
    <div
      className="absolute inset-0 touch-none select-none"
      role="presentation"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <video
        ref={video}
        src={`/videos/pack-open/${packId}${rare ? ".rare" : ""}.mp4?v=2`}
        autoPlay
        muted
        playsInline
        preload="auto"
        onEnded={onDone}
        onError={onDone}
        className="h-full w-full object-cover"
      />
      {/* 개봉 힌트 — 뜯기 전까지 펄스 */}
      <p
        className={cn(
          "pointer-events-none absolute top-[16%] left-1/2 -translate-x-1/2 animate-pulse font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80 transition-opacity duration-300",
          waiting ? "opacity-100" : "opacity-0",
        )}
      >
        Drag across the top to rip open
      </p>
    </div>
  );
}
